import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';

import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as RewardCardService from '../../api/rewardCardService';
import * as RewardService from '../../api/rewardService';

import Paper from '../Paper';
import useCompanyContext from '../../hooks/useCompanyContext';

const useRewardStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(3),
    width: 350,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  titleMinimize: {
    fontWeight: theme.typography.fontWeightRegular,
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionContainerCentered: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  showDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  showDetailButton: {
    color: theme.palette.text.secondary,
    padding: 5,
  },
  grow: {
    flexGrow: 1,
  },
  finePrint: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  finePrintCenter: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
    justifyContent: 'center',
    alignItems: 'center',
  },
  markdown: {
    marginTop: 10,
    marginBottom: 10,
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
  },
}));

export function RewardContainer({
  title,
  rewardId,
  handleRedeemReward,
  handleDeleteReward,
  expiryDate,
  isAdminOnly,
}: {
  title: string;
  rewardId: string;
  handleRedeemReward: () => void;
  handleDeleteReward: () => void;
  expiryDate: number;
  isAdminOnly: boolean;
}) {
  const { company } = useCompanyContext();
  const classes = useRewardStyles();
  const [reward, setReward] = useState<RewardService.Reward | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => {
    const fetchReward = async () => {
      if (company) {
        const res = await RewardService.get(company.id, rewardId);

        setReward(res);
      }
    };

    fetchReward();
  }, [rewardId, company]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.cardContainer}>
        <div className={classes.showDetailContainer}>
          <div className={classes.grow} />
          <Button
            variant="text"
            onClick={() => setShowDetails(!showDetails)}
            className={classes.showDetailButton}
          >
            {showDetails ? 'Hide' : 'Expand'}
          </Button>
        </div>
        <div className={classes.header}>
          <Typography variant="h5">{title}</Typography>
        </div>
        {!showDetails && (
          <div className={classes.finePrintCenter}>
            <Typography
              variant="body2"
              className={classes.titleMinimize}
              gutterBottom
            >
              <strong>Valid Until</strong>
            </Typography>
            <div>
              <Typography variant="body2" gutterBottom>
                {moment.unix(expiryDate).format('MMM DD, YYYY')}
              </Typography>
            </div>
          </div>
        )}
        {showDetails && (
          <div className={classes.finePrint}>
            <Typography variant="body2" className={classes.title} gutterBottom>
              <strong>What You'll Get</strong>
            </Typography>
            <div>
              <ReactMarkdown className={classes.markdown}>
                {reward.what}
              </ReactMarkdown>
            </div>
            <Typography variant="body2" className={classes.title} gutterBottom>
              <strong>The Fine Print</strong>
            </Typography>
            <div>
              <ReactMarkdown className={classes.markdown}>
                {reward.info}
              </ReactMarkdown>
            </div>
            <Typography variant="body2" className={classes.title} gutterBottom>
              <strong>Valid Until</strong>
            </Typography>
            <div>
              <Typography variant="body2" gutterBottom>
                {moment.unix(expiryDate).format('MMM DD, YYYY')}
              </Typography>
            </div>
          </div>
        )}
        <div
          className={
            isAdminOnly
              ? classes.actionContainer
              : classes.actionContainerCentered
          }
        >
          {isAdminOnly && (
            <Button
              className={classes.actionButton}
              onClick={handleDeleteReward}
            >
              Delete Reward
            </Button>
          )}
          <Button
            color="primary"
            className={classes.actionButton}
            onClick={handleRedeemReward}
          >
            Redeem Reward
          </Button>
        </div>
      </Paper>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

type Props = {
  rewardCardList: RewardCardService.RewardCard[];
  handleRedeemReward: (rewardCard: RewardCardService.RewardCard) => void;
  handleDeleteReward: (rewardCard: RewardCardService.RewardCard) => void;
  isAdminOnly: boolean;
};

export default function RewardCard({
  rewardCardList,
  handleRedeemReward,
  handleDeleteReward,
  isAdminOnly,
}: Props) {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.root}>
      {rewardCardList.map((rewardCard, idx) => (
        <RewardContainer
          key={idx}
          title={rewardCard.title}
          rewardId={rewardCard.reward.id}
          handleRedeemReward={() => {
            handleRedeemReward(rewardCard);
          }}
          handleDeleteReward={() => {
            handleDeleteReward(rewardCard);
          }}
          expiryDate={rewardCard.expiresAt}
          isAdminOnly={isAdminOnly}
        />
      ))}
    </Grid>
  );
}
